import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  RemoveRedEye,
} from '@mui/icons-material';
import adminService from '../../services/adminService';

const DocumentVerification = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState('pending');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await adminService.getDocuments({ status: 'pending' });
      setDocuments(response.data.documents);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerification = async () => {
    try {
      await adminService.verifyDocument(selectedDoc._id, {
        status: verificationStatus,
        notes,
        userId: selectedDoc.user._id,
      });
      setOpenDialog(false);
      fetchDocuments();
    } catch (error) {
      console.error('Error verifying document:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      default:
        return 'warning';
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 4 }}>
        Document Verification
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Review pending documents and approve or reject with clear notes.
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Filter by Type</InputLabel>
              <Select
                value="all"
                label="Filter by Type"
                onChange={() => {}}
              >
                <MenuItem value="all">All Documents</MenuItem>
                <MenuItem value="Adhaar">Adhaar Card</MenuItem>
                <MenuItem value="Pan">PAN Card</MenuItem>
                <MenuItem value="Passport">Passport</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button variant="contained" onClick={fetchDocuments}>
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Documents Grid */}
      <Grid container spacing={3}>
        {documents.map((doc) => (
          <Grid item xs={12} sm={6} md={4} key={doc._id}>
            <Card sx={{ borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {doc.verificationType}
                </Typography>
                <Typography color="text.secondary" gutterBottom>
                  User: {doc.user?.name}
                </Typography>
                <Typography color="text.secondary" gutterBottom>
                  Submitted: {new Date(doc.createdAt).toLocaleDateString()}
                </Typography>
                <Chip
                  label={doc.status}
                  color={getStatusColor(doc.status)}
                  size="small"
                />
              </CardContent>
              <CardActions sx={{ mt: 'auto', px: 2, pb: 2 }}>
                <Button
                  size="small"
                  startIcon={<RemoveRedEye />}
                  onClick={() => window.open(doc.verificationDocumentUrl, '_blank')}
                >
                  View
                </Button>
                <Button
                  size="small"
                  color="primary"
                  onClick={() => {
                    setSelectedDoc(doc);
                    setOpenDialog(true);
                  }}
                >
                  Verify
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Verification Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Verify Document</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={verificationStatus}
                label="Status"
                onChange={(e) => setVerificationStatus(e.target.value)}
              >
                <MenuItem value="approved">Approve</MenuItem>
                <MenuItem value="rejected">Reject</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleVerification} variant="contained">
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentVerification; 